/**
 * Utility functions for notifications
 * Centralized functions to avoid code duplication across components
 */

import {
  isSoccerV2FavoriteTeamNotification,
  getSoccerV2NotificationDisplay,
} from '@/domains/soccer-v2/utils/soccer-v2-notification-display';
import {
  parseNotificationData,
  getLiveStreamIdFromNotification,
  getLiveNotificationMessage,
  isLiveNotificationType,
  getLiveNotificationMessageKey,
  navigateToLiveFromNotification,
  normalizeNotificationPayload,
} from '@/domains/live/utils/liveNotificationUtils';

export {
  parseNotificationData,
  getLiveStreamIdFromNotification,
  getLiveNotificationMessage,
  isLiveNotificationType,
  navigateToLiveFromNotification,
  normalizeNotificationPayload,
};

/**
 * Get actor image URL
 * @param {Object|null} actor - The actor object
 * @param {Function} displayImage - The displayImage utility function
 * @returns {string} The image URL
 */
export function getActorImageUrl(actor, displayImage) {
  if (!actor) {
    return displayImage(null);
  }
  return displayImage(actor.profilePictureUrl);
}

/**
 * Get formatted actor name
 * @param {Object|null} actor - The actor object
 * @param {Function} t - The translation function
 * @returns {string} The formatted name
 */
export function getActorName(actor, t) {
  if (!actor) {
    return t('notifications.someone');
  }
  return actor.firstName && actor.lastName
    ? `${actor.firstName} ${actor.lastName}`
    : actor.username || t('notifications.someone');
}

/**
 * Format time since date (relative time)
 * @param {string} dateString - ISO date string
 * @param {Function} t - The translation function
 * @param {Object} options - Format options
 * @param {boolean} options.shortFormat - Use short format (m, h, d) instead of full format
 * @returns {string} Formatted time string
 */
export function formatTime(dateString, t, options = {}) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffMs / 604800000);
  const diffMonths = Math.floor(diffMs / 2592000000);

  // Short format (used in NotificationDropdown)
  if (options.shortFormat) {
    if (diffMins < 1) return t('notifications.time.justNow');
    if (diffMins < 60) return `${diffMins} m`;
    if (diffHours < 24) return `${diffHours} h`;
    if (diffDays < 7) return `${diffDays} d`;
    return date.toLocaleDateString();
  }

  // Full format (used in LatestActivitiesCard)
  if (diffMins < 1) return t('notifications.time.justNow');
  if (diffMins === 1) return `${diffMins} ${t('notifications.time.minuteAgo')}`;
  if (diffMins < 60) return `${diffMins} ${t('notifications.time.minutesAgo')}`;
  if (diffHours === 1) return `${diffHours} ${t('notifications.time.hourAgo')}`;
  if (diffHours < 24) return `${diffHours} ${t('notifications.time.hoursAgo')}`;
  if (diffDays === 1) return `${diffDays} ${t('notifications.time.dayAgo')}`;
  if (diffDays < 7) return `${diffDays} ${t('notifications.time.daysAgo')}`;
  if (diffWeeks === 1) return `${diffWeeks} ${t('notifications.time.weekAgo')}`;
  if (diffWeeks < 4) return `${diffWeeks} ${t('notifications.time.weeksAgo')}`;
  return `${diffMonths} ${t('notifications.time.monthsAgo')}`;
}

/**
 * Parse notification content into structured format
 * Separates userName and message for flexible rendering
 * @param {Object} notification - Notification object
 * @param {Function} t - Translation function
 * @returns {Object} { userName: string|null, message: string, position: 'prefix'|'suffix'|null }
 */
export function getParsedNotificationContent(notification, t) {
  const { type, reaction, data, actor } = notification;

  if (isSoccerV2FavoriteTeamNotification(notification)) {
    const { headline, detail, combined } = getSoccerV2NotificationDisplay(notification, t);
    if (detail && headline && headline !== detail) {
      return { userName: null, message: combined, position: null, soccerV2Headline: headline, soccerV2Detail: detail };
    }
    return { userName: null, message: combined || headline || detail || '', position: null };
  }

  // Get the full username
  const fullUserName = actor?.firstName && actor?.lastName
    ? `${actor.firstName} ${actor.lastName}`
    : actor?.username || t('notifications.someone');

  // Extract eventName for event notifications
  const eventName = data?.eventName || notification.event?.name || '';
  
  // Extract groupName for group notifications (try multiple sources)
  let groupName = '';
  if (notification?.data) {
    groupName = notification.data.groupName || notification.data.group?.name || '';
  }
  if (!groupName && data) {
    groupName = data.groupName || data.group?.name || '';
  }
  if (!groupName && notification?.group?.name) {
    groupName = notification.group.name;
  }
  // Fallback: extract from body if available (e.g., "requested to join the group \"ffhh\".")
  if (!groupName && notification?.body) {
    const match = notification.body.match(/group\s+"([^"]+)"/i);
    if (match && match[1]) {
      groupName = match[1];
    }
  }
  const groupNameText = groupName ? ` "${groupName}"` : '';

  // Backend-provided member invitation lifecycle messages should be displayed as-is.
  const pageMemberInviteLifecycleTypes = [
    'PAGE_MEMBER_ADDED',
    'PAGE_MEMBER_INVITE_ACCEPTED',
    'PAGE_MEMBER_INVITE_DECLINED'
  ];
  if (pageMemberInviteLifecycleTypes.includes(type) && notification?.body) {
    return { userName: null, message: notification.body, position: null };
  }

  // Types where userName comes at the end (suffix)
  const suffixTypes = ['NEW_MESSAGE', 'MESSAGE', 'INVITATION'];

  // Types with no userName at all
  const noUserNameTypes = [
    'TASK_REMINDER_DAILY', 'TASK_REMINDER_BEFORE_DEADLINE', 'TASK_REMINDER_CUSTOM',
    'BRONZE_BADGE', 'SILVER_BADGE', 'GOLD_BADGE', 'CAMPAIGN_FINISHED',
    'POST_PENDING_MODERATION', 'POST_APPROVED', 'POST_BANNED',
    'STORY_PENDING_MODERATION', 'STORY_APPROVED', 'STORY_BANNED',
    'COMMENT_PENDING_MODERATION', 'COMMENT_APPROVED', 'COMMENT_BANNED',
    'FAVORITE_TEAM_MATCH_STARTING', 'FAVORITE_TEAM_MATCH_STARTED', 'FAVORITE_TEAM_MATCH_FINISHED',
    'FAVORITE_TEAM_SCORED', 'FAVORITE_TEAM_WON', 'FAVORITE_TEAM_LOST', 'FAVORITE_TEAM_DREW',
    'FAVORITE_TEAM_MATCH_REMINDER'
  ];

  // Special cases with interpolated content
  const specialTypes = ['EVENT_CANCELLED', 'EVENT_UPDATED'];

  if (noUserNameTypes.includes(type)) {
    // No userName, just the message
    const messageMap = {
      TASK_REMINDER_DAILY: t('notifications.messages.TASK_REMINDER_DAILY'),
      TASK_REMINDER_BEFORE_DEADLINE: t('notifications.messages.TASK_REMINDER_BEFORE_DEADLINE'),
      TASK_REMINDER_CUSTOM: t('notifications.messages.TASK_REMINDER_CUSTOM'),
      BRONZE_BADGE: t('notifications.messages.BRONZE_BADGE'),
      SILVER_BADGE: t('notifications.messages.SILVER_BADGE'),
      GOLD_BADGE: t('notifications.messages.GOLD_BADGE'),
      CAMPAIGN_FINISHED: t('notifications.messages.CAMPAIGN_FINISHED'),
      POST_PENDING_MODERATION: t('moderation.postPending'),
      POST_APPROVED: (() => {
        const extractedGroupName = notification?.data?.groupName || notification?.data?.group?.name || '';
        return extractedGroupName ? `${t('moderation.postApproved')} "${extractedGroupName}"` : t('moderation.postApproved');
      })(),
      POST_BANNED: t('moderation.postBanned'),
      STORY_PENDING_MODERATION: t('moderation.storyPending'),
      STORY_APPROVED: t('moderation.storyApproved'),
      STORY_BANNED: t('moderation.storyBanned'),
      COMMENT_PENDING_MODERATION: t('moderation.commentPending'),
      COMMENT_APPROVED: t('moderation.commentApproved'),
      COMMENT_BANNED: t('moderation.commentBanned'),
      FAVORITE_TEAM_MATCH_STARTING: t('notifications.messages.FAVORITE_TEAM_MATCH_STARTING'),
      FAVORITE_TEAM_MATCH_STARTED: t('notifications.messages.FAVORITE_TEAM_MATCH_STARTED'),
      FAVORITE_TEAM_MATCH_FINISHED: t('notifications.messages.FAVORITE_TEAM_MATCH_FINISHED'),
      FAVORITE_TEAM_SCORED: t('notifications.messages.FAVORITE_TEAM_SCORED'),
      FAVORITE_TEAM_WON: t('notifications.messages.FAVORITE_TEAM_WON'),
      FAVORITE_TEAM_LOST: t('notifications.messages.FAVORITE_TEAM_LOST'),
      FAVORITE_TEAM_DREW: t('notifications.messages.FAVORITE_TEAM_DREW'),
      FAVORITE_TEAM_MATCH_REMINDER: t('notifications.messages.FAVORITE_TEAM_MATCH_REMINDER')
    };
    return { userName: null, message: messageMap[type] || '', position: null };
  }

  if (specialTypes.includes(type)) {
    // These have interpolated userName, return as full message
    const specialMap = {
      EVENT_CANCELLED: t('notifications.messages.EVENT_CANCELLED', { actor: fullUserName, eventName }),
      EVENT_UPDATED: t('notifications.messages.EVENT_UPDATED', { actor: fullUserName })
    };
    return { userName: null, message: specialMap[type] || '', position: null };
  }

  if (isLiveNotificationType(type)) {
    return { userName: null, message: getLiveNotificationMessage(notification, t, fullUserName), position: null };
  }

  if (suffixTypes.includes(type)) {
    // userName at the end
    const suffixMap = {
      NEW_MESSAGE: t('notifications.messages.NEW_MESSAGE'),
      MESSAGE: t('notifications.messages.MESSAGE'),
      INVITATION: t('notifications.messages.INVITATION')
    };
    return { userName: fullUserName, message: suffixMap[type] || '', position: 'suffix' };
  }

  // Default: userName at the beginning (prefix)
  const prefixMap = {
    POST_LIKE: t('notifications.messages.POST_LIKE'),
    COMMENT: t('notifications.messages.COMMENT'),
    REACTION_ADDED: reaction ? `${t('notifications.messages.REACTION_ADDED_WITH')} ${reaction}` : t('notifications.messages.REACTION_ADDED'),
    MESSAGE_REACTION: t('notifications.messages.MESSAGE_REACTION'),
    MENTION: t('notifications.messages.MENTION'),
    COMMENT_MENTION: t('notifications.messages.COMMENT_MENTION'),
    REPLY: t('notifications.messages.REPLY'),
    CONVERSATION_CREATED: t('notifications.messages.CONVERSATION_CREATED'),
    USER_ADDED_TO_CONVERSATION: t('notifications.messages.USER_ADDED_TO_CONVERSATION'),
    USER_REMOVED_FROM_CONVERSATION: t('notifications.messages.USER_REMOVED_FROM_CONVERSATION'),
    POST_SHARED: t('notifications.messages.POST_SHARED'),
    POST_TAGGED: t('notifications.messages.POST_TAGGED'),
    GROUP_TAGGED: t('notifications.messages.GROUP_TAGGED'),
    PAGE_TAGGED: t('notifications.messages.PAGE_TAGGED'),
    POST_UPDATED: t('notifications.messages.POST_UPDATED'),
    FRIEND_REQUEST: t('notifications.messages.FRIEND_REQUEST'),
    FRIEND_REQUEST_ACCEPTED: t('notifications.messages.FRIEND_REQUEST_ACCEPTED', { userName: '' }).replace(/\{\{userName\}\}/g, '').trim(),
    BIRTHDAY_REMINDER: t('notifications.messages.BIRTHDAY_REMINDER'),
    GROUP_INVITE_TO_GROUP: t('notifications.messages.GROUP_INVITE_TO_GROUP'),
    GROUP_JOIN_REQUEST: `${t('notifications.messages.GROUP_JOIN_REQUEST')}${groupNameText}`,
    GROUP_JOIN_REQUEST_ACCEPTED: t('notifications.messages.GROUP_JOIN_REQUEST_ACCEPTED'),
    GROUP_POST_CREATED: t('notifications.messages.GROUP_POST_CREATED'),
    GROUP_MEMBER_ADDED: t('notifications.messages.GROUP_MEMBER_ADDED'),
    PAGE_FOLLOW: t('notifications.messages.PAGE_FOLLOW'),
    PAGE_MEMBER_ADDED: t('notifications.messages.PAGE_MEMBER_ADDED'),
    PAGE_MEMBER_INVITE_ACCEPTED: t('notifications.messages.PAGE_MEMBER_INVITE_ACCEPTED'),
    PAGE_MEMBER_INVITE_DECLINED: t('notifications.messages.PAGE_MEMBER_INVITE_DECLINED'),
    PAGE_POST_CREATED: t('notifications.messages.PAGE_POST_CREATED'),
    PAGE_INVITE_TO_FOLLOW: t('notifications.messages.PAGE_INVITE_TO_FOLLOW'),
    PAGE_INVITE_ACCEPTED: t('notifications.messages.PAGE_INVITE_ACCEPTED'),
    PAGE_MEMBER_ROLE_UPDATED: t('notifications.messages.PAGE_MEMBER_ROLE_UPDATED'),
    EVENT_INVITATION: t('notifications.messages.EVENT_INVITATION', { eventName }),
    EVENT_INVITATION_ACCEPTED: t('notifications.messages.EVENT_INVITATION_ACCEPTED'),
    TASK_ASSIGNED: t('notifications.messages.TASK_ASSIGNED'),
    TASK_COMPLETED: t('notifications.messages.TASK_COMPLETED'),
    TASK_REOPENED: t('notifications.messages.TASK_REOPENED'),
    PROJECT_INVITATION: t('notifications.messages.PROJECT_INVITATION'),
    TASK_UPDATED: t('notifications.messages.TASK_UPDATED'),
    STORY_TAGGED: t('notifications.messages.STORY_TAGGED'),
    STORY_GROUP_TAGGED: t('notifications.messages.STORY_GROUP_TAGGED'),
    STORY_PAGE_TAGGED: t('notifications.messages.STORY_PAGE_TAGGED'),
    STORY_REACTION: t('notifications.messages.STORY_REACTION'),
    FAMILY_REQUEST: t('notifications.messages.FAMILY_REQUEST'),
    VOICE_CALL: t('notifications.messages.VOICE_CALL'),
    VIDEO_CALL: t('notifications.messages.VIDEO_CALL'),
  };

  const message = prefixMap[type] || t('notifications.messages.NEW_NOTIFICATION');
  return { userName: fullUserName, message: message, position: 'prefix' };
}

/**
 * Navigate to notification target routes
 * @param {Array} targets - Array of route objects to try
 * @param {Object} router - Vue Router instance
 * @returns {Promise<boolean>} Success status
 */
export async function navigateWithNotificationTargets(targets, router) {
  if (!targets || targets.length === 0) {
    return false;
  }

  for (const target of targets) {
    try {
      await router.push(target);
      return true;
    } catch (error) {
      if (error?.name === 'NavigationDuplicated') {
        return true;
      }
    }
  }

  return false;
}

